import { isValid, parse, format, parseISO } from 'date-fns';

export const formatDateInput = (
  input: string,
  isDeleting: boolean = false,
): string => {
  if (isDeleting) {
    if (input.match(/^\d{0,2}(\/\d{0,2})?(\/\d{0,4})?$/)) {
      return input;
    }
  }

  const numericOnly = input.replace(/\D/g, '');

  if (numericOnly.length <= 2) {
    return numericOnly;
  } else if (numericOnly.length <= 4) {
    return `${numericOnly.slice(0, 2)}/${numericOnly.slice(2)}`;
  } else {
    return `${numericOnly.slice(0, 2)}/${numericOnly.slice(2, 4)}/${numericOnly.slice(4, 8)}`;
  }
};

// parse MM/DD/YYYY string to Date
export const parseInputDate = (input: string): Date | null => {
  if (!input || input.replace(/\D/g, '').length < 8) return null;

  try {
    const parsedDate = parse(input, 'MM/dd/yyyy', new Date());
    return isValid(parsedDate) ? parsedDate : null;
  } catch {
    return null;
  }
};

// Format an ISO date/datetime string for display
export const formatDisplayDate = (
  value?: string | null,
  formatString: string = 'MM/dd/yyyy',
): string | undefined => {
  if (!value) return undefined;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return undefined;
    return format(date, formatString);
  } catch {
    return undefined;
  }
};

// Format a date-only yyyy-MM-dd string for display without UTC day shifts.
// Falls back to the original value when parsing fails.
export const formatDateOnly = (
  value?: string | null,
  formatString: string = 'MM/dd/yyyy',
): string | undefined => {
  if (!value) return undefined;
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? format(parsed, formatString) : value;
};

// Convert a Date (or parseable date string) into YYYY-MM-DD for form state
export const toIsoDateString = (value?: Date | string) => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    // Already in YYYY-MM-DD format (what our form state expects)
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return format(parsed, 'yyyy-MM-dd');
  }

  return format(value, 'yyyy-MM-dd');
};

// Format a YYYY-MM-DD ISO date string for display. Falls back to the raw
// string if parsing fails (e.g. a date range), or 'No data available' if empty.
export const formatISODate = (value: string | undefined | null): string => {
  if (!value) return 'No data available';
  const d = parseISO(value);
  return isValid(d) ? format(d, 'MM/dd/yyyy') : value;
};
